import { useEffect, useId, useState, type FormEvent } from 'react';
import { appel } from '../api';
import { Chargement, Erreur } from '../composants/Etat';
import { NavAdministration } from '../composants/NavAdministration';

interface PointDeVente {
  id: string;
  code: string;
  libelle: string;
  adresse: string | null;
  actif: boolean;
}

interface Famille {
  id: string;
  code: string;
  libelle: string;
  libelle_ar: string | null;
  ordre: number;
  actif: boolean;
}

function remplacerDans<T extends { id: string }>(liste: T[] | null, element: T): T[] {
  const autres = (liste ?? []).filter((e) => e.id !== element.id);
  return liste?.some((e) => e.id === element.id)
    ? (liste ?? []).map((e) => (e.id === element.id ? element : e))
    : [...autres, element];
}

/** Points de vente et métiers (familles d'affectation en base ; docs/UI.md § Écran 6). */
export function AdminReferentiels() {
  const [points, setPoints] = useState<PointDeVente[] | null>(null);
  const [familles, setFamilles] = useState<Famille[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      appel<PointDeVente[]>('/points-de-vente?inactifs=true'),
      appel<Famille[]>('/familles?inactives=true'),
    ])
      .then(([p, f]) => {
        setPoints(p);
        setFamilles(f);
      })
      .catch((e: Error) => setErreur(e.message));
  }, []);

  return (
    <section>
      <NavAdministration />
      <div className="entete-annonce">
        <h2>Points de vente et métiers</h2>
      </div>
      {erreur && <Erreur message={erreur} />}
      {message && (
        <p className="notice-cv" role="status">
          {message}
        </p>
      )}
      {!erreur && (!points || !familles) && <Chargement quoi="des référentiels" />}

      {points && (
        <section className="bloc">
          <h3>Points de vente</h3>
          <p className="mention">
            Un point de vente désactivé n’est plus proposé pour une annonce ni une embauche ; les fiches existantes le
            gardent. Le code, repris de l’Order Hub, ne se modifie pas.
          </p>
          <ul className="liste-admin">
            {points.map((point) => (
              <LignePointDeVente
                key={point.id}
                point={point}
                onModifie={(maj, texte) => {
                  setPoints((liste) => remplacerDans(liste, maj));
                  setMessage(texte);
                }}
              />
            ))}
          </ul>
          <NouveauPointDeVente
            onCree={(cree) => {
              setPoints((liste) => remplacerDans(liste, cree));
              setMessage(`Point de vente ${cree.code} créé.`);
            }}
          />
        </section>
      )}

      {familles && (
        <section className="bloc">
          <h3>Métiers</h3>
          <p className="mention">
            Un métier désactivé n’est plus proposé pour une nouvelle annonce. Pas d’ajout ici : chaque métier est
            lié à sa charte et à son barème par défaut, définis avec l’application.
          </p>
          <ul className="liste-admin">
            {familles.map((famille) => (
              <LigneFamille
                key={famille.id}
                famille={famille}
                onModifie={(maj, texte) => {
                  setFamilles((liste) => remplacerDans(liste, maj).sort((a, b) => a.ordre - b.ordre));
                  setMessage(texte);
                }}
              />
            ))}
          </ul>
        </section>
      )}
    </section>
  );
}

function LignePointDeVente({
  point,
  onModifie,
}: {
  point: PointDeVente;
  onModifie: (point: PointDeVente, texte: string) => void;
}) {
  const id = useId();
  const [edition, setEdition] = useState(false);
  const [libelle, setLibelle] = useState(point.libelle);
  const [adresse, setAdresse] = useState(point.adresse ?? '');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  async function envoyer(champs: { libelle: string; adresse: string | null; actif: boolean }, texte: string): Promise<boolean> {
    setEnvoi(true);
    setErreur(null);
    try {
      const maj = await appel<PointDeVente>(`/points-de-vente/${point.id}`, {
        method: 'PUT',
        body: JSON.stringify(champs),
      });
      onModifie(maj, texte);
      return true;
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Modification impossible');
      return false;
    } finally {
      setEnvoi(false);
    }
  }

  async function enregistrer(evenement: FormEvent): Promise<void> {
    evenement.preventDefault();
    if (await envoyer({ libelle, adresse: adresse.trim() || null, actif: point.actif }, `Point de vente ${point.code} modifié.`)) {
      setEdition(false);
    }
  }

  async function basculer(): Promise<void> {
    if (point.actif && !window.confirm(`Désactiver ${point.libelle} ? Il ne sera plus proposé.`)) return;
    // Bascule seule : une saisie en cours, non enregistrée, ne part pas avec elle.
    await envoyer(
      { libelle: point.libelle, adresse: point.adresse, actif: !point.actif },
      point.actif ? `${point.libelle} désactivé.` : `${point.libelle} réactivé.`,
    );
  }

  return (
    <li className={point.actif ? undefined : 'inactif'}>
      <div className="ligne-admin">
        <code>{point.code}</code>
        <strong>{point.libelle}</strong>
        {!point.actif && <span className="etiquette alerte-etiquette">Désactivé</span>}
        <div className="actions">
          <button type="button" className="bouton-second" disabled={envoi} onClick={() => setEdition((v) => !v)}>
            {edition ? 'Fermer' : 'Modifier'}
          </button>
          <button type="button" className="bouton-second" disabled={envoi} onClick={() => void basculer()}>
            {point.actif ? 'Désactiver' : 'Réactiver'}
          </button>
        </div>
      </div>
      {point.adresse && <p className="mention">{point.adresse}</p>}
      {edition && (
        <form className="formulaire-admin" noValidate onSubmit={(e) => void enregistrer(e)}>
          <div className="champ">
            <label htmlFor={`${id}-libelle`}>Libellé</label>
            <input id={`${id}-libelle`} value={libelle} maxLength={120} onChange={(e) => setLibelle(e.target.value)} />
          </div>
          <div className="champ">
            <label htmlFor={`${id}-adresse`}>Adresse</label>
            <input id={`${id}-adresse`} value={adresse} maxLength={300} onChange={(e) => setAdresse(e.target.value)} />
          </div>
          <div className="boutons">
            <button type="submit" className="bouton" disabled={envoi}>
              Enregistrer
            </button>
          </div>
        </form>
      )}
      {erreur && <Erreur message={erreur} />}
    </li>
  );
}

function NouveauPointDeVente({ onCree }: { onCree: (point: PointDeVente) => void }) {
  const id = useId();
  const [code, setCode] = useState('');
  const [libelle, setLibelle] = useState('');
  const [adresse, setAdresse] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  async function creer(evenement: FormEvent): Promise<void> {
    evenement.preventDefault();
    setEnvoi(true);
    setErreur(null);
    try {
      const cree = await appel<PointDeVente>('/points-de-vente', {
        method: 'POST',
        body: JSON.stringify({ code, libelle, adresse: adresse.trim() || null }),
      });
      onCree(cree);
      setCode('');
      setLibelle('');
      setAdresse('');
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Création impossible');
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <form className="formulaire-admin" noValidate onSubmit={(e) => void creer(e)}>
      <div className="champ">
        <label htmlFor={`${id}-code`}>Code (2 à 6 lettres ou chiffres)</label>
        <input
          id={`${id}-code`}
          value={code}
          maxLength={6}
          autoCapitalize="characters"
          onChange={(e) => setCode(e.target.value.toUpperCase())}
        />
      </div>
      <div className="champ">
        <label htmlFor={`${id}-libelle`}>Libellé</label>
        <input id={`${id}-libelle`} value={libelle} maxLength={120} onChange={(e) => setLibelle(e.target.value)} />
      </div>
      <div className="champ pleine">
        <label htmlFor={`${id}-adresse`}>Adresse</label>
        <input id={`${id}-adresse`} value={adresse} maxLength={300} onChange={(e) => setAdresse(e.target.value)} />
      </div>
      <div className="boutons">
        <button type="submit" className="bouton" disabled={envoi}>
          Ajouter le point de vente
        </button>
      </div>
      {erreur && <Erreur message={erreur} />}
    </form>
  );
}

function LigneFamille({ famille, onModifie }: { famille: Famille; onModifie: (famille: Famille, texte: string) => void }) {
  const id = useId();
  const [edition, setEdition] = useState(false);
  const [libelle, setLibelle] = useState(famille.libelle);
  const [libelleAr, setLibelleAr] = useState(famille.libelle_ar ?? '');
  const [ordre, setOrdre] = useState(String(famille.ordre));
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  async function envoyer(
    champs: { libelle: string; libelleAr: string | null; ordre: number; actif: boolean },
    texte: string,
  ): Promise<boolean> {
    setEnvoi(true);
    setErreur(null);
    try {
      const maj = await appel<Famille>(`/familles/${famille.id}`, {
        method: 'PUT',
        body: JSON.stringify(champs),
      });
      onModifie(maj, texte);
      return true;
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Modification impossible');
      return false;
    } finally {
      setEnvoi(false);
    }
  }

  async function enregistrer(evenement: FormEvent): Promise<void> {
    evenement.preventDefault();
    const rang = Number(ordre);
    if (ordre.trim() === '' || !Number.isInteger(rang) || rang < 0) {
      setErreur('Ordre : un entier positif');
      return;
    }
    const champs = { libelle, libelleAr: libelleAr.trim() || null, ordre: rang, actif: famille.actif };
    if (await envoyer(champs, `Métier ${famille.code} modifié.`)) setEdition(false);
  }

  async function basculer(): Promise<void> {
    if (famille.actif && !window.confirm(`Désactiver le métier ${famille.libelle} ? Il ne sera plus proposé.`)) return;
    await envoyer(
      { libelle: famille.libelle, libelleAr: famille.libelle_ar, ordre: famille.ordre, actif: !famille.actif },
      famille.actif ? `${famille.libelle} désactivé.` : `${famille.libelle} réactivé.`,
    );
  }

  return (
    <li className={famille.actif ? undefined : 'inactif'}>
      <div className="ligne-admin">
        <code>{famille.code}</code>
        <strong>{famille.libelle}</strong>
        {famille.libelle_ar && (
          <span lang="ar" dir="rtl">
            {famille.libelle_ar}
          </span>
        )}
        {!famille.actif && <span className="etiquette alerte-etiquette">Désactivé</span>}
        <div className="actions">
          <button type="button" className="bouton-second" disabled={envoi} onClick={() => setEdition((v) => !v)}>
            {edition ? 'Fermer' : 'Modifier'}
          </button>
          <button type="button" className="bouton-second" disabled={envoi} onClick={() => void basculer()}>
            {famille.actif ? 'Désactiver' : 'Réactiver'}
          </button>
        </div>
      </div>
      {edition && (
        <form className="formulaire-admin" noValidate onSubmit={(e) => void enregistrer(e)}>
          <div className="champ">
            <label htmlFor={`${id}-libelle`}>Libellé en français</label>
            <input id={`${id}-libelle`} value={libelle} maxLength={120} onChange={(e) => setLibelle(e.target.value)} />
          </div>
          <div className="champ">
            <label htmlFor={`${id}-ar`}>Libellé en arabe</label>
            <input
              id={`${id}-ar`}
              lang="ar"
              dir="rtl"
              value={libelleAr}
              maxLength={120}
              onChange={(e) => setLibelleAr(e.target.value)}
            />
          </div>
          <div className="champ">
            <label htmlFor={`${id}-ordre`}>Ordre d’affichage</label>
            <input
              id={`${id}-ordre`}
              type="number"
              inputMode="numeric"
              min={0}
              step={1}
              value={ordre}
              onChange={(e) => setOrdre(e.target.value)}
            />
          </div>
          <div className="boutons">
            <button type="submit" className="bouton" disabled={envoi}>
              Enregistrer
            </button>
          </div>
        </form>
      )}
      {erreur && <Erreur message={erreur} />}
    </li>
  );
}
